import { useId, useMemo } from 'react';
import { dataResponseSchema } from '@/core';
import { DataSnapshotMetadataContext, useSnapshotQuery } from '../DataSnapshotProvider/DataSnapshotContext';
import { SnapshotID } from '../DataSnapshotProvider/model';
import { useTimeRange } from '../TimeRangeProviderV2';
import { DataQueriesContext } from './DataQueriesContext';
import { DataQueriesProviderProps, UseDataQueryResult } from './model';

interface SnapshotDataQueryProviderProps extends Pick<DataQueriesProviderProps, 'id'> {
  children?: React.ReactNode;
  snapshotId: SnapshotID;
}

/**
 * Provider that loads data from a snapshot instead of running queries.
 * Used when a snapshotId is provided in the options.
 */
export function SnapshotDataQueryProvider({ children, snapshotId, id: initId }: SnapshotDataQueryProviderProps) {
  const fallbackId = useId();
  const id = initId ?? fallbackId;

  // Fetch the snapshot using React Query
  const { data: snapshot, isLoading, isFetching, error, refetch } = useSnapshotQuery(snapshotId);

  // Run each result through the schema to ensure it's valid
  const parsedDataResponses = useMemo<UseDataQueryResult['queryResults']>(() => {
    if (snapshot === undefined) return [];

    return snapshot.results.map((result) => {
      const { dataResponse } = result;
      try {
        const parsedResponse = dataResponseSchema.parse(dataResponse);
        return {
          ...parsedResponse,
          error: null,
          isFetching: false,
          isFetchingMore: false,
          isLoading: false,
          refetch: undefined,
        };
      } catch (_error) {
        // TODO: Figure out how we want to handle this.  Error out the entire snapshot?
        return {
          ...dataResponse,
          error: null,
          isFetching: false,
          isFetchingMore: false,
          isLoading: false,
          refetch: undefined,
        };
      }
    });
  }, [snapshot]);

  const { refreshKey } = useTimeRange();

  /*
  Just a note here that we include the refresh key in this result object because some visualizations (timeseries)
  might not properly handle a time range change (the case I saw was on a time range zoom out) if the query result
  object does not change.  This workaround should be cheap because the query above is not rerunning, so this just 
  rebuilding an object with the same data.
  */
  const value: UseDataQueryResult = useMemo(() => {
    const results = parsedDataResponses ?? [];

    return {
      queryKey: `${snapshotId}:${refreshKey}`,
      queryResults: error ? [] : results,
      isFetching,
      isFetchingMore: false,
      isLoading,
      refetchAll: refetch,
      errors: error ? [error] : [],
      pagination: undefined,
      queryMode: 'active',
      id,
      cancelInProgressDataQueries: async () => {},
    };
  }, [snapshotId, id, isLoading, isFetching, error, refetch, parsedDataResponses, refreshKey]);

  return (
    <DataQueriesContext.Provider value={value}>
      <DataSnapshotMetadataContext.Provider value={snapshot?.metadata}>{children}</DataSnapshotMetadataContext.Provider>
    </DataQueriesContext.Provider>
  );
}
